import React, { useState, useEffect } from 'react';
import api from '../services/api';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const PROMPT_PADRAO = `Você é um assistente virtual do escritório jurídico MJ. Seu papel é atender leads que chegam pelo WhatsApp com cordialidade e eficiência.

COMPORTAMENTO:
- Cumprimente pelo nome quando disponível
- Use linguagem simples, sem termos jurídicos complicados
- Seja objetivo mas empático — muitos leads estão em situação difícil
- Nunca prometa resultados ou valores

CAMPANHAS DISPONÍVEIS:
- Auxílio Doença: para quem está afastado por doença
- Auxílio Acidente: para quem sofreu acidente de trabalho
- Vínculo Empregatício: para quem trabalhou sem carteira assinada
- BPC Loas: para pessoas com deficiência ou idosos sem renda
- Salário Maternidade: para trabalhadoras rurais e urbanas

COLETA DE DADOS:
Após identificar o interesse, pergunte:
1. Nome completo
2. CPF
3. Breve descrição da situação

Quando tiver os 3 dados, diga exatamente: "QUALIFICADO" (isso aciona a transferência para o atendente).`;

export default function ConfigIA() {
  const [config,    setConfig]    = useState(null);
  const [salvando,  setSalvando]  = useState(false);
  const [testando,  setTestando]  = useState(false);
  const [salvo,     setSalvo]     = useState(false);
  const [erro,      setErro]      = useState('');
  const [testeResp, setTesteResp] = useState('');
  const [diasAtivos,setDiasAtivos]= useState([1,2,3,4,5]);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    try {
      const { data } = await api.get('/ia/config');
      const c = data.config;
      setConfig(c);
      setDiasAtivos(c.dias_semana ? c.dias_semana.split(',').map(Number) : [1,2,3,4,5]);
    } catch {
      // Se não encontrar, usa defaults
      setConfig({
        ativo: false, so_fora_horario: false, transferir_apos: true,
        provedor: 'anthropic', modelo: 'claude-sonnet-4-6',
        api_key_mascarada: '', prompt: PROMPT_PADRAO,
        boas_vindas: 'Olá! 👋 Sou o assistente virtual do escritório MJ. Como posso te ajudar?',
        msg_transfer: 'Perfeito! Vou chamar um especialista. Aguarde um momento! 🙂',
        horario_inicio: '08:00', horario_fim: '18:00',
      });
    }
  }

  function atualizar(campo, valor) {
    setConfig(prev => ({ ...prev, [campo]: valor }));
  }

  function toggleDia(dia) {
    setDiasAtivos(prev =>
      prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia].sort()
    );
  }

  async function salvar() {
    setErro(''); setSalvando(true);
    try {
      await api.put('/ia/config', {
        ...config,
        dias_semana: diasAtivos.join(','),
      });
      setSalvo(true);
      setTimeout(() => setSalvo(false), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar.');
    }
    setSalvando(false);
  }

  async function testar() {
    setErro(''); setTestando(true); setTesteResp('');
    try {
      const { data } = await api.post('/ia/testar', { telefone: 'teste' });
      setTesteResp(data.resposta_ia);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro no teste.');
    }
    setTestando(false);
  }

  if (!config) return (
    <div style={s.loading}>
      <i className="ti ti-loader-2" style={{ fontSize: 28, animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>
            <i className="ti ti-robot" style={{ fontSize: 18, verticalAlign: -3 }} />
            {' '}Agente de IA
          </div>
          <div style={s.sub}>Configure o assistente automático que atende os leads</div>
        </div>
        <span style={{ ...s.badge, ...(config.ativo ? s.badgeOn : s.badgeOff) }}>
          <span style={s.dot} />
          {config.ativo ? 'Ativo' : 'Inativo'}
        </span>
      </div>

      {/* Card: Ativar */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <i className="ti ti-toggle-right" style={{ fontSize: 16 }} /> Ativação
        </div>
        <div style={s.cardSub}>Controle quando o agente deve atuar</div>

        <Toggle
          label="Ativar agente de IA"
          sub="O agente responde automaticamente quando chegar mensagem"
          value={config.ativo}
          onChange={v => atualizar('ativo', v)}
        />
        <Toggle
          label="Atuar apenas fora do horário comercial"
          sub="Durante o horário configurado abaixo, os atendentes respondem normalmente"
          value={config.so_fora_horario}
          onChange={v => atualizar('so_fora_horario', v)}
        />
        <Toggle
          label="Transferir para atendente após qualificação"
          sub='Quando a IA escrever "QUALIFICADO" na resposta, o atendente é acionado'
          value={config.transferir_apos}
          onChange={v => atualizar('transferir_apos', v)}
          last
        />
      </div>

      {/* Card: Modelo */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <i className="ti ti-cpu" style={{ fontSize: 16 }} /> Modelo de IA
        </div>
        <div style={s.cardSub}>Escolha qual inteligência artificial o agente vai usar</div>

        <Field label="Provedor">
          <select
            style={s.input}
            value={config.provedor}
            onChange={e => {
              atualizar('provedor', e.target.value);
              atualizar('modelo', e.target.value === 'anthropic' ? 'claude-sonnet-4-6' : 'gpt-4o');
            }}
          >
            <option value="anthropic">Anthropic (Claude) — Recomendado</option>
            <option value="openai">OpenAI (ChatGPT)</option>
          </select>
        </Field>

        <Field label="Modelo">
          <select style={s.input} value={config.modelo} onChange={e => atualizar('modelo', e.target.value)}>
            {config.provedor === 'anthropic' ? <>
              <option value="claude-sonnet-4-6">Claude Sonnet 4.6 — Melhor qualidade</option>
              <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 — Mais rápido e barato</option>
            </> : <>
              <option value="gpt-4o">GPT-4o — Melhor qualidade</option>
              <option value="gpt-4o-mini">GPT-4o Mini — Mais rápido e barato</option>
            </>}
          </select>
        </Field>

        <Field label="Chave de API">
          <input
            type="password"
            style={s.input}
            placeholder={config.api_key_mascarada || (config.provedor === 'anthropic' ? 'sk-ant-...' : 'sk-...')}
            onChange={e => atualizar('api_key', e.target.value)}
          />
          <div style={s.hint}>
            {config.provedor === 'anthropic'
              ? 'Obtenha em console.anthropic.com → API Keys'
              : 'Obtenha em platform.openai.com → API Keys'
            }
          </div>
        </Field>
      </div>

      {/* Card: Prompt */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <i className="ti ti-message-2" style={{ fontSize: 16 }} /> Instruções do agente
        </div>
        <div style={s.cardSub}>
          Escreva em português como o agente deve se comportar.
          Quando o lead estiver qualificado, instrua o agente a escrever a palavra <strong>QUALIFICADO</strong>.
        </div>

        <Field label="Prompt (instruções principais)">
          <textarea
            style={{ ...s.input, minHeight: 260, resize: 'vertical', lineHeight: 1.65 }}
            value={config.prompt || ''}
            onChange={e => atualizar('prompt', e.target.value)}
          />
          <div style={{ ...s.hint, textAlign: 'right' }}>
            {(config.prompt || '').length.toLocaleString('pt-BR')} caracteres
          </div>
        </Field>

        <Field label="Mensagem de boas-vindas">
          <textarea
            style={{ ...s.input, minHeight: 70, resize: 'vertical' }}
            value={config.boas_vindas || ''}
            onChange={e => atualizar('boas_vindas', e.target.value)}
          />
          <div style={s.hint}>Enviada automaticamente na primeira mensagem do lead</div>
        </Field>

        <Field label="Mensagem ao transferir para atendente" last>
          <textarea
            style={{ ...s.input, minHeight: 60, resize: 'vertical' }}
            value={config.msg_transfer || ''}
            onChange={e => atualizar('msg_transfer', e.target.value)}
          />
          <div style={s.hint}>Enviada quando o lead for qualificado e o atendente for acionado</div>
        </Field>
      </div>

      {/* Card: Horário */}
      <div style={s.card}>
        <div style={s.cardTitle}>
          <i className="ti ti-clock" style={{ fontSize: 16 }} /> Horário de funcionamento
        </div>
        <div style={s.cardSub}>Usado quando "atuar apenas fora do horário" está ativado</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <Field label="Abertura">
            <input type="time" style={s.input} value={config.horario_inicio || '08:00'}
              onChange={e => atualizar('horario_inicio', e.target.value)} />
          </Field>
          <Field label="Fechamento">
            <input type="time" style={s.input} value={config.horario_fim || '18:00'}
              onChange={e => atualizar('horario_fim', e.target.value)} />
          </Field>
        </div>

        <Field label="Dias de funcionamento" last>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            {DIAS.map((d, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleDia(i)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 99,
                  border: `1.5px solid ${diasAtivos.includes(i) ? '#25D366' : 'var(--color-border-tertiary)'}`,
                  background: diasAtivos.includes(i) ? '#25D366' : 'transparent',
                  color: diasAtivos.includes(i) ? '#fff' : 'var(--color-text-secondary)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {d}
              </button>
            ))}
          </div>
        </Field>
      </div>

      {/* Resultado do teste */}
      {testeResp && (
        <div style={s.testeBox}>
          <div style={s.testeTitle}>
            <i className="ti ti-robot" style={{ fontSize: 14 }} /> Resposta do agente no teste:
          </div>
          <div style={s.testeResp}>{testeResp}</div>
        </div>
      )}

      {/* Erro */}
      {erro && (
        <div style={s.erroBox}>
          <i className="ti ti-alert-circle" style={{ fontSize: 14 }} /> {erro}
        </div>
      )}

      {/* Ações */}
      <div style={s.actions}>
        <button style={s.btnSave} onClick={salvar} disabled={salvando}>
          <i className="ti ti-device-floppy" style={{ fontSize: 15 }} />
          {salvando ? 'Salvando...' : 'Salvar configurações'}
        </button>
        <button style={s.btnTest} onClick={testar} disabled={testando}>
          <i className="ti ti-player-play" style={{ fontSize: 15 }} />
          {testando ? 'Testando...' : 'Testar agente'}
        </button>
        {salvo && (
          <span style={s.savedMsg}>
            <i className="ti ti-check" style={{ fontSize: 13 }} /> Salvo com sucesso!
          </span>
        )}
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Toggle({ label, sub, value, onChange, last }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: last ? 0 : 16 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>{sub}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{
          width: 42, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer',
          background: value ? '#25D366' : 'var(--color-border-secondary)',
          position: 'relative', flexShrink: 0, transition: 'background 0.2s', marginLeft: 16,
        }}
      >
        <span style={{
          position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: '#fff',
          top: 3, left: value ? 21 : 3, transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

function Field({ label, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const s = {
  page:    { flex: 1, overflowY: 'auto', padding: 24, background: 'var(--color-background-tertiary)' },
  loading: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' },
  header:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  title:   { fontSize: 18, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 },
  sub:     { fontSize: 12, color: 'var(--color-text-secondary)' },
  badge:   { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 99 },
  badgeOn: { background: '#dcfce7', color: '#14532d' },
  badgeOff:{ background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' },
  dot:     { width: 6, height: 6, borderRadius: '50%', background: 'currentColor' },
  card:    { background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 'var(--border-radius-lg)', padding: 20, marginBottom: 14 },
  cardTitle:{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 7 },
  cardSub: { fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 16 },
  input:   { width: '100%', padding: '8px 11px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', fontSize: 12, color: 'var(--color-text-primary)', fontFamily: 'var(--font-sans)' },
  hint:    { fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 },
  actions: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, paddingBottom: 32 },
  btnSave: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, border: 'none', background: '#25D366', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnTest: { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  savedMsg:{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#14532d', background: '#dcfce7', padding: '6px 12px', borderRadius: 99 },
  testeBox:{ background: '#f0fdf4', border: '0.5px solid #86efac', borderRadius: 10, padding: 16, marginBottom: 14 },
  testeTitle:{ fontSize: 12, fontWeight: 500, color: '#15803d', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 },
  testeResp:{ fontSize: 13, color: '#14532d', lineHeight: 1.6 },
  erroBox: { background: '#fef2f2', border: '0.5px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 7 },
};
